import { bool, func } from "prop-types";
import React, { useRef } from "react";
import { twMerge } from "tailwind-merge";

import ContentWrapper from "@components/shared/ContentWrapper";
import Customize from "../Customize/Customize";
import CarGallery from "../CarGallery/CarGallery";
import DiscountStripe from "@components/DiscountStripe/DiscountStripe";
import PreconfigurationList from "../PreconfigurationPicker/PreconfigurationList";
import CarouselAndFinanceBar from "../CarouselAndFinanceBar/CarouselAndFinanceBar";
import CompareButtonContainer from "@components/CompareButtonContainer/CompareButtonContainer";
import MobileContainer from "@components/shared/MobileContainer";
import CollapsableContainerPreconfigurations from "../CarouselAndFinanceBar/CollapsableContainerPreconfigurations";

import {
  useDiscountStripeSelector,
  useEcoBonusSelector,
  useIsAnyCEESubsidyEnabledForJourney,
  useMovesProgramSelector,
} from "@redux/reducers/appConfig";
import { useJourneySpecificScrappage } from "@hooks/useJourneySpecificScrappage";

import { useIsScrappageSelectedSelector } from "@redux/reducers/deal";
import { useIsFixed } from "@hooks/useIsFixed";

const CarConfigurator = ({
  isFinancingOpen,
  isPromoCodeApplicable,
  onIsFinancingOpenChange,
}) => {
  const isScrappageSelected = useIsScrappageSelectedSelector();

  const discountStripe = useDiscountStripeSelector();
  const ecoBonus = useEcoBonusSelector();

  const scrappage = useJourneySpecificScrappage();

  const isDiscountStripeEnabled = discountStripe?.enabled;

  const movesProgram = useMovesProgramSelector();
  const isMovesProgramEnabled = movesProgram?.enabled;

  const priceDetailRef = useRef(null);
  const priceDetailHeightRef = useRef(0);
  const priceDetailPositionerRef = useRef(null);

  const isCEESubsidyEnabled = useIsAnyCEESubsidyEnabledForJourney();

  if (priceDetailRef.current) {
    const { height } = priceDetailRef.current.getBoundingClientRect();
    priceDetailHeightRef.current = height;
  }

  const isFixed = useIsFixed(() => {
    if (!priceDetailPositionerRef.current) {
      return false;
    }

    const { y } = priceDetailPositionerRef.current.getBoundingClientRect();

    return y < 0;
  });

  return (
    <ContentWrapper dataId="car-configurator-container">
      <div className="flex flex-col justify-between gap-8 w-full lg:grid lg:grid-cols-[5fr_4fr] lg:py-4">
        <div
          className="flex flex-col justify-start items-center overflow-hidden w-full min-w-0"
          data-id="car-selection-wrapper"
        >
          <MobileContainer className="flex-col">
            <CollapsableContainerPreconfigurations>
              {({ isOpen }) => (
                <>
                  <PreconfigurationList isOpen={isOpen} />
                  <CompareButtonContainer />
                </>
              )}
            </CollapsableContainerPreconfigurations>
          </MobileContainer>

          <CarGallery />

          <div className="md:hidden" ref={priceDetailPositionerRef} />

          {isFixed && <div style={{ height: priceDetailHeightRef.current }} />}

          <MobileContainer className="flex-col">
            <div
              className={twMerge(
                "w-full z-10",
                isFixed ? "fixed top-0" : "relative"
              )}
              id="helper"
              ref={priceDetailRef}
            >
              <CarouselAndFinanceBar
                isPromoCodeApplicable={isPromoCodeApplicable}
                onIsFinancingOpenChange={onIsFinancingOpenChange}
              />
              {(isDiscountStripeEnabled ||
                isMovesProgramEnabled ||
                isCEESubsidyEnabled) && (
                <DiscountStripe
                  isScrappageSelected={isScrappageSelected}
                  isScrappageEnabled={scrappage.enabled}
                  governmentDiscountPercentage={
                    ecoBonus.governmentDiscountPercentage
                  }
                  scrappageDiscountPercentage={scrappage.discountPercentage}
                  withEcoBonus={ecoBonus.enabled}
                  isMovesProgramEnabled={isMovesProgramEnabled}
                />
              )}
            </div>
          </MobileContainer>
        </div>
        <div className="relative w-full min-w-0">
          <div className="hidden lg:block sticky top-58 -mt-4 mb-8 z-5">
            {(isDiscountStripeEnabled || isMovesProgramEnabled) && (
              <DiscountStripe
                isScrappageSelected={isScrappageSelected}
                isScrappageEnabled={scrappage.enabled}
                governmentDiscountPercentage={
                  ecoBonus.governmentDiscountPercentage
                }
                scrappageDiscountPercentage={scrappage.discountPercentage}
                withEcoBonus={ecoBonus.enabled}
                isMovesProgramEnabled={isMovesProgramEnabled}
              />
            )}
          </div>
          <Customize
            isFinancingOpen={isFinancingOpen}
            isPromoCodeApplicable={isPromoCodeApplicable}
            onIsFinancingOpenChange={onIsFinancingOpenChange}
            fixedHeaderHeight={priceDetailHeightRef.current}
          />
        </div>
      </div>
    </ContentWrapper>
  );
};

CarConfigurator.propTypes = {
  isFinancingOpen: bool.isRequired,
  onIsFinancingOpenChange: func.isRequired,
  isPromoCodeApplicable: bool,
};

export default React.memo(CarConfigurator);
